import {
  Button,
  Dialog,
  DialogBareContent,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Textarea,
} from '@h2hspark/ui';
import { Check, X } from 'lucide-react';
import * as React from 'react';

import { useReturnFocusToOpener } from '../../member/use-overlay-focus-return';

import {
  NOTE_COMPOSER_CANCEL_LABEL,
  NOTE_COMPOSER_CLOSE_LABEL,
  NOTE_COMPOSER_FIELD_LABEL,
  NOTE_COMPOSER_PRIVACY_LINE,
  NOTE_COMPOSER_REMOVE_LABEL,
  NOTE_COMPOSER_SAVE_LABEL,
  QUICK_NOTE_MAX_LENGTH,
  QUICK_NOTE_PLACEHOLDER,
  noteComposerTitle,
} from './constants';

/**
 * **The note composer** — `Web App.dc.html`'s `noteOpen` overlay (§2.16), reached from a met-person
 * card's note or its *Add a note* (`MEM-029` AC-3, `MEM-EVT-007`).
 *
 * ## It writes the same note the capture sheet does
 *
 * One mutation, `useAnnotateConnection`, and therefore one note per connection. The capture sheet
 * offers it *"immediately after logging"*, which is when it is worth most; this is the same field
 * an hour later, when the member remembers what they meant to write down. A second write path would
 * be a second note.
 *
 * ## A centred dialog on desktop and a bottom sheet on a phone (`MEM-056` AC-3)
 *
 * The overlay table puts the note composer at 440px centred from `md`, and as a sheet below it —
 * the phone design's 26px top radius and grab handle. One element at both widths, so nothing exists
 * at one and not the other.
 *
 * ## Removing a note is not removing a connection
 *
 * The prototype draws a **Delete** control when a note exists, and it deletes the *note*. It is
 * kept, and it is deliberately worded *Remove note*: `MEM-031` AC-4 says there is no undo on a
 * logged connection, and a control reading *Delete* on a card about a person is exactly the one a
 * member would misread as removing them.
 */
export function NoteComposerDialog({
  person,
  busy,
  onSave,
  onClose,
}: {
  readonly person: {
    readonly attendeeRef: string;
    readonly firstName: string;
    readonly note: string | null;
  } | null;
  readonly busy: boolean;
  readonly onSave: (input: { readonly attendeeRef: string; readonly note: string }) => void;
  readonly onClose: () => void;
}): React.ReactElement | null {
  const [draft, setDraft] = React.useState('');
  const fieldRef = React.useRef<HTMLTextAreaElement>(null);

  // Seeded when the composer opens, and never re-seeded while it is open: the member's edits are
  // theirs, and a refetch landing mid-sentence must not overwrite what they are typing.
  React.useEffect(() => {
    if (person !== null) setDraft(person.note ?? '');
  }, [person]);

  // **Above the early return, because it is a hook.** This overlay mounts already open — the
  // component renders nothing until `person` is set — so Radix recorded no element to return focus
  // to and the keyboard landed on `<body>`. The control that opened it is remembered here instead;
  // see the hook's header.
  const { onCloseAutoFocus } = useReturnFocusToOpener(person !== null);

  if (person === null) return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className="z-[60] bg-[rgba(28,14,48,.5)] backdrop-blur-none" />
        <DialogBareContent
          data-testid="note-composer"
          onCloseAutoFocus={onCloseAutoFocus}
          onOpenAutoFocus={(opening) => {
            opening.preventDefault();
            fieldRef.current?.focus();
          }}
          className="fixed inset-x-0 bottom-0 z-[61] max-h-[92vh] overflow-y-auto rounded-t-[26px] bg-card px-5 pb-8 pt-5 shadow-xl md:inset-x-0 md:bottom-auto md:top-10 md:mx-auto md:w-full md:max-w-[440px] md:rounded-xl md:px-6 md:pb-6 md:pt-5"
        >
          {/* **One close control at both widths**, rather than the phone design's grab handle and
              the desktop's `x`. A class that removes an element above or below a line has to be
              listed in `responsive-behaviour.ts` with a sentence about what the member on the other
              side of it can no longer do — and here the honest sentence is *nothing*, because
              Cancel, the scrim and Escape all close this at every width. That file's own rule then
              applies: if the sentence is "nothing", the difference is layout and does not belong on
              an element that disappears. What differs by width is the shape — a sheet off the
              bottom edge on a phone, a centred 440px card from `md` (`MEM-056` AC-3). */}
          <div className="mb-3 flex items-start justify-between gap-3">
            <DialogTitle className="text-lg">{noteComposerTitle(person.firstName)}</DialogTitle>
            <DialogClose
              aria-label={NOTE_COMPOSER_CLOSE_LABEL}
              className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-pill text-text-subtle hover:bg-muted"
            >
              <X className="h-[18px] w-[18px]" aria-hidden />
            </DialogClose>
          </div>

          <p className="mb-1.5 font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle">
            {NOTE_COMPOSER_FIELD_LABEL}
          </p>
          {/* Focused on open: the composer has one field and the member opened it to type in
              that field. Radix would otherwise move focus to the dialog's first tabbable node,
              which is the close control — a sheet that opens on a phone with the keyboard down and
              the cursor on "close" is a sheet somebody has to tap twice. */}
          <Textarea
            ref={fieldRef}
            value={draft}
            maxLength={QUICK_NOTE_MAX_LENGTH}
            placeholder={QUICK_NOTE_PLACEHOLDER}
            aria-label={NOTE_COMPOSER_FIELD_LABEL}
            className="min-h-28"
            onChange={(changed) => {
              setDraft(changed.target.value);
            }}
          />
          <p className="mt-2 text-xs text-muted-foreground">{NOTE_COMPOSER_PRIVACY_LINE}</p>

          <div className="mt-5 flex flex-wrap gap-2.5">
            <Button
              className="flex-1"
              disabled={busy}
              onClick={() => {
                onSave({ attendeeRef: person.attendeeRef, note: draft.trim() });
              }}
            >
              <Check className="mr-1.5 h-4 w-4" aria-hidden />
              {draft.trim().length === 0 && person.note !== null
                ? NOTE_COMPOSER_REMOVE_LABEL
                : NOTE_COMPOSER_SAVE_LABEL}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={onClose}>
              {NOTE_COMPOSER_CANCEL_LABEL}
            </Button>
          </div>
        </DialogBareContent>
      </DialogPortal>
    </Dialog>
  );
}
